/* ************************************************************************** */
/*                                Dependencies                                */
/* ************************************************************************** */

// Lib dependencies
import { useCallback, useMemo, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { showNotification } from '@mantine/notifications';

// UI lib components
import { Stack, Container, Group, ActionIcon, Box, Alert } from '@mantine/core';

// UI local components
import ConfirmBar from '@ui-kit/molecules/ConfirmBar';
import UserTile from '@app/shared/ui-kit/atoms/UserTile';
import UserPicker from '@app/features/User/UserPicker';

// Store
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import dialogStoreVar from '@features/Dialog/store/dialogStore';

// Hooks
import { useWorkspaceUsers } from '@features/User/hooks/useWorkspaceUsers';
import useRefetchQuestions from '@features/Support/hooks/useRefetchQuestions';

// Assets
import {
  IconUserPlus as IconSelectOwner,
  IconTrash as IconRemoveOwner,
  IconAlertCircle,
} from '@tabler/icons-react';

// Local API & Types
import {
  PermissionKey,
  useAssignOwnerToQuestionMutation,
  useTopicsQuery,
  useUpdateTopicUserAssignmentsMutation,
} from '@app-types/blackroom-api';
import { DialogQuestionOwnerProps, OwnerBaseInfo } from './types';

// Helpers
import { suggestWorkflowFromQuestions } from '@features/Support/helpers/invokeWorkflowSuggestion';

// Style
import { ICON_SIZE_MAP } from '@app/shared/ui-kit/icons-sizes';

/* ************************************************************************** */
/*                                  Component                                 */
/* ************************************************************************** */

function DialogQuestionOwner({
  questionIds,
  questions: questionsProp,
  assignedOwners,
  isSelectAll,
  scope,
}: DialogQuestionOwnerProps) {
  /* ********************************** HOOKS ********************************* */

  // State
  const [owners, setOwners] = useState<OwnerBaseInfo[]>(assignedOwners ?? []);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Store
  const dialogStore = useReactiveVar(dialogStoreVar);
  const { closeDialog } = dialogStore ?? {};
  const workspace = useWorkspace();

  const { data: workspaceUsers } = useWorkspaceUsers({
    withPermission: PermissionKey.CanAnswerQuestions,
  });

  const availableUsers = useMemo(() => {
    return workspaceUsers.filter(
      (user) => !owners.find((owner) => owner.id === user.id),
    );
  }, [workspaceUsers, owners]);

  // API
  const [assignOwner] = useAssignOwnerToQuestionMutation();
  const { data: topicsData } = useTopicsQuery({
    variables: { workspaceId: workspace.id },
  });
  const [updateTopicUserAssignments] = useUpdateTopicUserAssignmentsMutation();

  const refetchQuestions = useRefetchQuestions();

  // Locale
  const { t } = useTranslation(['Common', 'Support']);

  /* ******************************** CALLBACKS ******************************* */

  const handleAddOwner = useCallback(
    (ownerEmail: string) => {
      const ownerToAdd = availableUsers.find(
        (user) => user.email === ownerEmail,
      );
      if (ownerToAdd) {
        setOwners((prevOwners) => [...prevOwners, ownerToAdd]);
      }
    },
    [setOwners, availableUsers],
  );

  const handleRemoveOwner = useCallback(
    (ownerToRemove: OwnerBaseInfo) => {
      setOwners((prevOwners) =>
        prevOwners.filter((owner) => owner.id !== ownerToRemove.id),
      );
    },
    [setOwners],
  );

  async function onSubmit() {
    try {
      closeDialog?.('dialog-support-question-owner');
      await assignOwner({
        variables: {
          assignOwnerInput: {
            questionIds,
            userIds: owners.map((owner) => owner.id),
            shouldRetainOldOwners: false,
            workspaceId: workspace.id,
            isSelectAll,
            scope,
          },
        },
      });
      refetchQuestions();

      // Suggest workflow linkage for newly assigned owners
      if (questionsProp && questionsProp.length > 0) {
        suggestWorkflowFromQuestions({
          questions: questionsProp,
          currentOwnerIds: owners.map((owner) => owner.id),
          previousOwnerIds: (assignedOwners ?? []).map((o) => o.id),
          workspaceId: workspace.id,
          topicsData,
          updateTopicUserAssignments,
          t,
        });
      }
    } catch (error) {
      showNotification({
        title: t('Common:errors.genericTitle'),
        message: t('Common:errors.genericMessage'),
        color: 'blackroomRed',
      });
      console.error(error);
    }
  }

  /* ******************************** RENDERING ******************************* */

  return (
    <>
      {/* Invisible overlay to intercept clicks when dropdown is open */}
      {isDropdownOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 250, // Below the dropdown but above the modal
            backgroundColor: 'transparent',
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsDropdownOpen(false);
          }}
        />
      )}

      <Container
        w="30vw"
        onKeyDown={function cb(e) {
          switch (e.key) {
            case 'Enter':
              onSubmit();
              break;
            default:
              break;
          }
        }}
        fluid
      >
        <Stack align="stretch" h="100%">
          <form onSubmit={(e) => e.preventDefault()}>
            <Stack>
              <Stack gap="xs">
                {owners.map((owner) => (
                  <Group key={owner.id} justify="space-between">
                    <Box>
                      <UserTile user={owner} />
                    </Box>
                    <ActionIcon
                      variant="subtle"
                      color="blackroomGray"
                      onClick={() => handleRemoveOwner(owner)}
                    >
                      <IconRemoveOwner size={ICON_SIZE_MAP.small} />
                    </ActionIcon>
                  </Group>
                ))}
              </Stack>
              <UserPicker
                key="user-picker"
                availableUsers={availableUsers}
                allowCustomEmail
                onPickUser={handleAddOwner}
                leftSection={<IconSelectOwner size={ICON_SIZE_MAP.small} />}
                w="100%"
                onOpen={() => setIsDropdownOpen(true)}
                onClose={() => setIsDropdownOpen(false)}
              />
            </Stack>
          </form>

          {owners.length === 0 && (
            <Alert
              variant="light"
              color="blackroomRed"
              title={t(
                'Support:PageSupport.supportActions.dialogQuestionOwner.alert.title',
              )}
              icon={<IconAlertCircle />}
              p="xs"
            >
              {t(
                'Support:PageSupport.supportActions.dialogQuestionOwner.alert.text',
              )}
            </Alert>
          )}

          <ConfirmBar
            labelProps={{
              confirm: t('Common:cta.save'),
              cancel: t('Common:cta.cancel'),
            }}
            onConfirm={onSubmit}
            onCancel={() => {
              dialogStore?.closeDialog?.('dialog-support-question-owner');
            }}
          />
        </Stack>
      </Container>
    </>
  );
}

export default DialogQuestionOwner;
